import type { Feature, FeatureCollection, Geometry } from 'geojson'

type GeometryWithCoordinates = Geometry & { coordinates?: unknown }
type GenericProperties = Record<string, unknown>
type MapboxSplitCache = WeakMap<FeatureCollection, MapboxGeometryCollections>
type BoundsCache = WeakMap<FeatureCollection, GeoJsonBounds | null>

export interface GeoJsonBounds {
  sw: [number, number]
  ne: [number, number]
}

export interface MapboxGeometryCollections {
  points: FeatureCollection
  lines: FeatureCollection
  polygons: FeatureCollection
}

const EMPTY_COLLECTION: FeatureCollection = {
  type: 'FeatureCollection',
  features: []
}

const splitCache: MapboxSplitCache = new WeakMap()
const boundsCache: BoundsCache = new WeakMap()

const normalizeLongitudeToPositive = (longitude: number): number => {
  const normalized = longitude % 360
  return normalized < 0 ? normalized + 360 : normalized
}

const resolveShortestLongitudeBounds = (longitudes: number[]): [number, number] | null => {
  if (longitudes.length === 0) {
    return null
  }

  if (longitudes.length === 1) {
    return [longitudes[0], longitudes[0]]
  }

  let minLongitude = Infinity
  let maxLongitude = -Infinity
  for (let index = 0; index < longitudes.length; index += 1) {
    const longitude = longitudes[index]
    if (longitude < minLongitude) minLongitude = longitude
    if (longitude > maxLongitude) maxLongitude = longitude
  }

  const directSpan = maxLongitude - minLongitude

  const normalizedLongitudes = longitudes.map(normalizeLongitudeToPositive).sort((left, right) => left - right)

  let largestGap = -1
  let largestGapIndex = -1

  for (let index = 0; index < normalizedLongitudes.length; index += 1) {
    const current = normalizedLongitudes[index]
    const next =
      index === normalizedLongitudes.length - 1 ? normalizedLongitudes[0] + 360 : normalizedLongitudes[index + 1]
    const gap = next - current

    if (gap > largestGap) {
      largestGap = gap
      largestGapIndex = index
    }
  }

  const arcStart = normalizedLongitudes[(largestGapIndex + 1) % normalizedLongitudes.length]
  const arcEnd = normalizedLongitudes[largestGapIndex]
  const swLongitude = arcStart
  const neLongitude = arcEnd < arcStart ? arcEnd + 360 : arcEnd
  const wrappedSpan = neLongitude - swLongitude

  if (wrappedSpan >= directSpan) {
    return [minLongitude, maxLongitude]
  }

  return [swLongitude, neLongitude]
}

const cloneFeatureWithGeometry = (feature: Feature, geometry: Geometry, suffix: string): Feature => {
  const baseId = String(feature.id ?? feature.properties?.id ?? '')
  const featureId = baseId ? `${baseId}${suffix}` : undefined
  const sourceProperties =
    feature.properties && typeof feature.properties === 'object' ? (feature.properties as GenericProperties) : null
  const props: GenericProperties = {}

  if (featureId) props.id = featureId
  if (sourceProperties) {
    Object.assign(props, sourceProperties)
  }

  return {
    type: 'Feature',
    ...(featureId ? { id: featureId } : {}),
    geometry,
    properties: props
  }
}

const walkGeometry = (geometry: Geometry, callback: (geometry: Geometry) => void) => {
  if (geometry.type !== 'GeometryCollection') {
    callback(geometry)
    return
  }

  const stack: Geometry[] = []
  for (let index = geometry.geometries.length - 1; index >= 0; index -= 1) {
    const nested = geometry.geometries[index]
    if (!nested) {
      continue
    }
    stack.push(nested)
  }

  while (stack.length > 0) {
    const currentGeometry = stack.pop()
    if (!currentGeometry) {
      continue
    }

    if (currentGeometry.type === 'GeometryCollection') {
      for (let index = currentGeometry.geometries.length - 1; index >= 0; index -= 1) {
        const nested = currentGeometry.geometries[index]
        if (!nested) {
          continue
        }
        stack.push(nested)
      }
      continue
    }

    callback(currentGeometry)
  }
}

const accumulateBoundsFromCoordinates = (
  coordinates: unknown,
  updateBounds: (longitude: number, latitude: number) => void
) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return
  }

  const stack: unknown[] = [coordinates]

  while (stack.length > 0) {
    const current = stack.pop()
    if (!Array.isArray(current) || current.length === 0) {
      continue
    }

    if (typeof current[0] === 'number') {
      const longitude = Number(current[0])
      const latitude = Number(current[1])
      if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
        updateBounds(longitude, latitude)
      }
      continue
    }

    for (let index = current.length - 1; index >= 0; index -= 1) {
      stack.push(current[index])
    }
  }
}

export const splitGeoJsonForMapbox = (geojson: FeatureCollection): MapboxGeometryCollections => {
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
    return {
      points: { ...EMPTY_COLLECTION },
      lines: { ...EMPTY_COLLECTION },
      polygons: { ...EMPTY_COLLECTION }
    }
  }

  const cached = splitCache.get(geojson)
  if (cached) {
    return cached
  }

  const points: Feature[] = []
  const lines: Feature[] = []
  const polygons: Feature[] = []

  for (let featureIndex = 0; featureIndex < geojson.features.length; featureIndex += 1) {
    const feature = geojson.features[featureIndex]
    if (!feature?.geometry) {
      continue
    }

    let splitIndex = 0
    walkGeometry(feature.geometry, (geometry) => {
      const suffix = `__${featureIndex}_${splitIndex}`
      splitIndex += 1

      if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
        points.push(cloneFeatureWithGeometry(feature, geometry, suffix))
        return
      }

      if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        lines.push(cloneFeatureWithGeometry(feature, geometry, suffix))
        return
      }

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        polygons.push(cloneFeatureWithGeometry(feature, geometry, suffix))
      }
    })
  }

  const split: MapboxGeometryCollections = {
    points: {
      type: 'FeatureCollection',
      features: points
    },
    lines: {
      type: 'FeatureCollection',
      features: lines
    },
    polygons: {
      type: 'FeatureCollection',
      features: polygons
    }
  }

  splitCache.set(geojson, split)
  return split
}

export const computeGeoJsonBounds = (geojson: FeatureCollection): GeoJsonBounds | null => {
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
    return null
  }

  const cached = boundsCache.get(geojson)
  if (cached !== undefined) {
    return cached
  }

  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  let hasCoordinatesInBounds = false
  const longitudes: number[] = []

  const updateBounds = (longitude: number, latitude: number) => {
    hasCoordinatesInBounds = true
    longitudes.push(longitude)
    if (longitude < minLng) minLng = longitude
    if (latitude < minLat) minLat = latitude
    if (longitude > maxLng) maxLng = longitude
    if (latitude > maxLat) maxLat = latitude
  }

  for (let featureIndex = 0; featureIndex < geojson.features.length; featureIndex += 1) {
    const feature = geojson.features[featureIndex]
    if (!feature?.geometry) {
      continue
    }

    walkGeometry(feature.geometry, (geometry) => {
      const coordinates = (geometry as GeometryWithCoordinates).coordinates
      accumulateBoundsFromCoordinates(coordinates, updateBounds)
    })
  }

  if (!hasCoordinatesInBounds) {
    boundsCache.set(geojson, null)
    return null
  }

  const shortestLongitudeBounds = resolveShortestLongitudeBounds(longitudes)
  const [resolvedSwLongitude, resolvedNeLongitude] = shortestLongitudeBounds ?? [minLng, maxLng]

  const bounds: GeoJsonBounds = {
    sw: [resolvedSwLongitude, minLat],
    ne: [resolvedNeLongitude, maxLat]
  }

  boundsCache.set(geojson, bounds)
  return bounds
}
